import {Trans, useTranslation} from '@/translations/i18n'
import {ProjectExtended} from '@/features/projects/types'
import {MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {useDialogStore} from '@ariakit/react'
import {FormProvider, useForm, Controller} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import React, {useMemo} from 'react'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {LiftSuspensionProjectFormValidationSchema, NOTES_MAX_LENGTH} from '../../formSchemas'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {format} from 'date-fns'
import {SlashCircle01Icon, Trash01Icon} from '@/components/ui/icon/Icon'
import {useEditProjectDetails} from '../../queries/useEditProjectDetails'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {suspendReasonToLabel} from '../../utils'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {Button} from '@/components/ui/button/Button'
import {useDeleteProjectSuspension} from '../../queries/useDeleteProjectSuspension'
import {toast} from '@/utilities/toast/toast'
import dayjs from '@/dayjs'

interface ProjectOptionLiftSuspensionDialogProps {
    project: ProjectExtended
}

export const ProjectOptionLiftSuspensionDialog = ({project}: ProjectOptionLiftSuspensionDialogProps) => {
    const {t} = useTranslation()

    // startDate === today is treated as active, not future
    const earliestFutureSuspension = useMemo(() => {
        const future = project.suspensions?.filter(s => dayjs(s.startDate).isAfter(dayjs(), 'day')) ?? []
        return future.sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)))[0]
    }, [project.suspensions])

    const hasSuspensionsInFuture = !!earliestFutureSuspension

    const suspension = useMemo(() => {
        return project.suspensions?.[0]
    }, [project.suspensions])

    const form = useForm<LiftSuspensionProjectFormValidationSchema>({
        resolver: zodResolver(LiftSuspensionProjectFormValidationSchema.partial()),
        defaultValues: {
            suspensionEndDate: suspension?.endDate ? new Date(suspension.endDate) : undefined,
            suspensionNotes: suspension?.notes ?? undefined
        }
    })
    const dialogStore = useDialogStore({
        setOpen: () => {
            form.reset()
        }
    })
    const deleteDialogStore = useDialogStore()

    const queryClient = useQueryClient()

    const {mutate: editProjectDetails, isPending: isEditing} = useEditProjectDetails({
        urlParams: {projectId: project.id},
        options: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, project.patient?.id]})
                dialogStore.setOpen(false)
            }
        }
    })

    const {mutate: deleteSuspension, isPending: isDeletingPending} = useDeleteProjectSuspension(
        project.id,
        earliestFutureSuspension?.id ?? '',
        {
            onError: errorHandler,
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                toast.success(t('projects:delete_future_suspension_title'))
                deleteDialogStore.setOpen(false)
            }
        }
    )

    const handleConfirm = form.handleSubmit(data => {
        editProjectDetails({
            suspensionEndDate: data.suspensionEndDate ? format(data.suspensionEndDate, 'yyyy-MM-dd') : undefined,
            suspensionNotes: data.suspensionNotes ?? undefined,
            notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
            notificationDetails: data.notificationDetails
        })
    })

    const isDirty = ObjectKeys(form.formState.dirtyFields).length > 0

    return (
        <>
            <MenuItem
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dialogStore.setOpen(true)
                }}
            >
                <SlashCircle01Icon />
                {t('projects:change_status_manage_suspension')}
            </MenuItem>
            <FormProvider {...form}>
                <ConfirmationDialog
                    dialogStore={dialogStore}
                    onConfirm={handleConfirm}
                    isConfirmPending={isEditing}
                    hideDialogBody
                    confirmButtonDisabled={!isDirty || isDeletingPending}
                    headerSlot={
                        <Flexbox direction="column" gap={7} fullWidth>
                            <Flexbox direction="column" fullWidth gap={2}>
                                <DialogHeading>{t('projects:change_status_manage_suspension')}</DialogHeading>
                                <Flexbox direction="column" fullWidth gap={1}>
                                    {project.patient && project && (
                                        <Text palette="neutral" shade="950" size="textMd">
                                            <Trans
                                                i18nKey="agenda:pri_n_expiration_date"
                                                values={{
                                                    code: project.code || '-',
                                                    date: project.expirationDate
                                                        ? format(new Date(project.expirationDate), 'dd/MM/yyyy')
                                                        : '-'
                                                }}
                                            />
                                        </Text>
                                    )}
                                    <Text palette="neutral" shade="600" size="textSm">
                                        {t('commons:patient_fullname_value', {fullName: project.patient?.fullName})}
                                    </Text>
                                </Flexbox>
                            </Flexbox>
                        </Flexbox>
                    }
                    contentSlot={
                        <Flexbox direction="column" fullWidth gap={4}>
                            <Grid columns={2} gap={4}>
                                <Flexbox direction="column" fullWidth gap={1}>
                                    <Text palette="neutral" shade="500" size="textSm">
                                        {t('projects:suspend_start_date')}
                                    </Text>
                                    <Text palette="neutral" shade="900" size="textSm" weight="500">
                                        {suspension?.startDate
                                            ? format(new Date(suspension.startDate), 'dd/MM/yyyy')
                                            : '-'}
                                    </Text>
                                </Flexbox>
                                <Controller
                                    control={form.control}
                                    name="suspensionEndDate"
                                    render={({field, fieldState}) => (
                                        <InputDate
                                            label={t('projects:suspend_end_date')}
                                            placeholder={'GG/MM/AAAA'}
                                            errorMessage={fieldState.error?.message}
                                            disableBeforeDate={
                                                suspension?.startDate ? new Date(suspension.startDate) : undefined
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                                <GridItem spanColumn={2}>
                                    <Flexbox direction="column" fullWidth gap={1}>
                                        <Text palette="neutral" shade="500" size="textSm">
                                            {t('projects:suspend_reason')}
                                        </Text>
                                        <Text palette="neutral" shade="900" size="textSm" weight="500">
                                            {suspension?.reason ? t(suspendReasonToLabel[suspension.reason]) : '-'}
                                        </Text>
                                    </Flexbox>
                                </GridItem>
                                <GridItem spanColumn={2}>
                                    <Flexbox direction="column" fullWidth gap={1}>
                                        <Text palette="neutral" shade="500" size="textSm">
                                            {t('commons:notes')}
                                        </Text>
                                        <Controller
                                            control={form.control}
                                            name="suspensionNotes"
                                            render={({field, fieldState}) => (
                                                <TextArea
                                                    errorMessage={fieldState.error?.message}
                                                    maxLength={NOTES_MAX_LENGTH}
                                                    placeholder={t('commons:notes_placeholder')}
                                                    helpText={`${field.value?.length ?? 0}/${NOTES_MAX_LENGTH}`}
                                                    value={field.value ?? ''}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </Flexbox>
                                </GridItem>
                            </Grid>
                        </Flexbox>
                    }
                    footerLeadingSlot={
                        hasSuspensionsInFuture && !isDirty ? (
                            <Button
                                variant="ghostDanger"
                                disabled={isDeletingPending}
                                onClick={() => deleteDialogStore.setOpen(true)}
                            >
                                <Trash01Icon />
                                {t('commons:delete')}
                            </Button>
                        ) : undefined
                    }
                />
            </FormProvider>
            <ConfirmationDialog
                dialogStore={deleteDialogStore}
                onConfirm={() => deleteSuspension()}
                isConfirmPending={isDeletingPending}
                confirmButtonVariant="primaryDanger"
                confirmButtonText={t('commons:confirm')}
                headerSlot={
                    <Flexbox direction="column" gap={7} fullWidth>
                        <Flexbox direction="column" fullWidth gap={2}>
                            <DialogHeading>{t('projects:delete_future_suspension_title')}</DialogHeading>
                            <Text palette="neutral" shade="600" size="textSm">
                                {t('commons:patient_fullname_value', {fullName: project.patient?.fullName})}
                            </Text>
                        </Flexbox>
                    </Flexbox>
                }
                contentSlot={
                    <Text palette="neutral" shade="950" size="textSm">
                        {t('projects:delete_future_suspension_confirm')}
                    </Text>
                }
            />
        </>
    )
}
